import { getTabId } from "./tabId";

const ENABLED = process.env.NODE_ENV !== "production";
const LEGACY_KEY = "trackDebugLog";
export const KEY_PREFIX = "trackDebugLog:";
const MAX_ENTRIES = 5000;
const KEEP_OTHER_TABS_MS = 7 * 24 * 60 * 60 * 1000;
const COLUMNS = ["local_time", "at", "session_id", "tab", "page", "part", "name", "action", "value", "by", "workflow_name", "workflow_type", "text", "unnamed", "form", "component_id", "workflow_key", "detail"];

const pad = (number) => String(number).padStart(2, "0");

export const toLocalTime = (iso) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const parseRows = (raw) => {
  try {
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const tabId = getTabId();
const ownKey = KEY_PREFIX + tabId;

export { getTabId };

const otherKeys = () => {
  const keys = [];
  try {
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key && key.startsWith(KEY_PREFIX) && key !== ownKey) keys.push(key);
    }
  } catch (error) {
    return [];
  }
  return keys;
};

const load = () => {
  if (!ENABLED) return [];
  try {
    const own = parseRows(localStorage.getItem(ownKey));
    const legacy = parseRows(sessionStorage.getItem(LEGACY_KEY));
    if (legacy.length) sessionStorage.removeItem(LEGACY_KEY);
    return [...legacy, ...own];
  } catch (error) {
    return [];
  }
};

const remove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    return;
  }
};

const read = (key) => {
  try {
    return parseRows(localStorage.getItem(key));
  } catch (error) {
    return [];
  }
};

const pruneOldTabs = () => {
  const cutoff = Date.now() - KEEP_OTHER_TABS_MS;
  otherKeys().forEach((key) => {
    const rows = read(key);
    const last = rows.length ? new Date(rows[rows.length - 1].at).getTime() : 0;
    if (!rows.length || last < cutoff) remove(key);
  });
};

let entries = load();
if (ENABLED) pruneOldTabs();

const persist = () => {
  try {
    localStorage.setItem(ownKey, JSON.stringify(entries));
  } catch (error) {
    return;
  }
};

export const recordDebug = (row = {}) => {
  if (!ENABLED) return;
  const hasSession = row.session_id !== undefined && row.session_id !== null && row.session_id !== "";
  const at = row.at || new Date().toISOString();
  entries.push({ ...row, at, tab: tabId, local_time: toLocalTime(at), session_id: hasSession ? String(row.session_id) : null });
  if (entries.length > MAX_ENTRIES) entries = entries.slice(entries.length - MAX_ENTRIES);
  persist();
};

const allRows = () => {
  const others = otherKeys().flatMap(read);
  return [...entries, ...others].sort((a, b) => String(a.at).localeCompare(String(b.at)));
};

export const getDebugLog = () => allRows();

export const clearDebugLog = () => {
  entries = [];
  otherKeys().forEach(remove);
  persist();
};

const toCsv = (rows) => {
  const cell = (value) => {
    if (value === undefined || value === null) return "";
    const text = typeof value === "object" ? JSON.stringify(value) : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };
  return [COLUMNS.join(","), ...rows.map((row) => COLUMNS.map((column) => cell(row[column])).join(","))].join("\n");
};

export const downloadDebugLog = (format = "csv") => {
  const isCsv = format !== "json";
  const rows = allRows();
  const content = isCsv ? toCsv(rows) : JSON.stringify(rows, null, 2);
  const blob = new Blob([content], { type: isCsv ? "text/csv" : "application/json" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `animo-session-log-${new Date().toISOString().replace(/[:.]/g, "-")}.${isCsv ? "csv" : "json"}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
};

export const listUnnamed = (rows) => {
  const counts = {};
  (rows || allRows())
    .filter((row) => row.unnamed)
    .forEach((row) => {
      const where = [row.page, row.part, row.text || row.name].join(" | ");
      const key = row.component_id || where;
      if (!counts[key]) counts[key] = { where, component_id: row.component_id, times: 0 };
      counts[key].times += 1;
    });
  return Object.keys(counts)
    .map((key) => counts[key])
    .sort((a, b) => b.times - a.times);
};
